import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Autocomplete, Icon, Tag, LegacyStack, Banner } from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { FormFunnel } from "components/form-funnel";
import {
  FunnelConfig,
  FunnelStep,
  CustomComponentProps,
} from "components/form-funnel/types";
import { step1Steps } from "containers/my-onboarding/onboarding-configs";
import {
  createOnboarding,
  fetchOnboardingById,
  updateOnboarding,
} from "services/onboarding.service";
import { fetchOnboardingsThunk } from "containers/onboardings/onboardings.slice";
import { fetchOnboardingDetails } from "containers/onboarding-view-container/onboarding-container.slice";
import { AppDispatch, RootState } from "configureStore";
import { canManageOnboarding } from "utils/onboardingPermissions";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import UserSelect from "features/user-select/user-select";
import { toast } from "sonner";
import { STEP_2_FILE_TYPES } from "utils/onboardingStatus";
import { fetchFileCategoryById } from "services/files-category.service";
import { fetchById as fetchUserById } from "services/user-select.service";
import { fetchById as fetchCompanyById } from "services/company.service";
import { OnboardingMode } from "types/Onboarding";
import DepartmentMultiSelect from "components/department-multi-select/department-multi-select";
import PositionsFunnelField from "features/positions/PositionsFunnelField";
import { fetchUserPositions, parsePositionIds } from "services/positions.service";
import { decidePositionsPrefill } from "features/positions/positions-helpers";
import { errorCode } from "features/positions/positions-errors";
import { onboardingDepartmentErrorKey } from "containers/departments/department-errors";
import {
  fetchAssignableDepartments,
  AssignableDepartmentsResponse,
} from "services/departments.service";
import {
  buildSalaryChangePayload,
  salaryChangeErrorMessageKey,
  validateSalaryChangePayload,
} from "containers/my-onboarding/salary-change-helpers";
import { alreadyEmployedHereMessage } from "utils/onboarding-errors";

interface OnboardingFormProps {
  id?: string;
}

/**
 * The email names someone who works here now: re-thrown for FormFunnel as
 * the text key plus the person's name (FormFunnel shows t(message, values)).
 */
function throwIfAlreadyEmployedHere(e: any) {
  const message = alreadyEmployedHereMessage(e);
  if (message) {
    throw Object.assign(new Error(message.key), { values: message.values });
  }
}

type ContractCategoryIds = {
  standard: string;
  director: string;
};

type DepartmentOption = {
  id: string;
  name: string;
};

// ─── File Select (filtered by category) ───────────────────────────
function FileSelect({
  value,
  onChange,
  label,
  isRequired,
  categoryId,
}: {
  value: string;
  onChange: (fileId: string) => void;
  label?: string;
  isRequired?: boolean;
  categoryId?: string;
}) {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    []
  );
  const [selectedLabel, setSelectedLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasLoadedCategory, setHasLoadedCategory] = useState(false);
  const [query, setQuery] = useState("");

  // Load files from the contract category
  useEffect(() => {
    if (!companyId || !categoryId) {
      setOptions([]);
      setSelectedLabel("");
      setHasLoadedCategory(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setHasLoadedCategory(false);
      try {
        const data = await fetchFileCategoryById(companyId, categoryId);
        if (cancelled) return;
        const files: any[] = data?.filesCollection || data?.files || [];
        setOptions(
          files.map((f: any) => ({
            value: f.id,
            label: f.title || f.name || f.id,
          }))
        );
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHasLoadedCategory(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, categoryId]);

  // Keep selection aligned with currently available category files.
  useEffect(() => {
    if (!hasLoadedCategory) return;
    if (!value) {
      if (selectedLabel) setSelectedLabel("");
      return;
    }

    const found = options.find((o) => o.value === value);
    if (found) {
      setSelectedLabel(found.label);
      return;
    }

    setSelectedLabel("");
    onChange("");
  }, [hasLoadedCategory, value, selectedLabel, options, onChange]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const handleSelect = useCallback(
    (selected: string[]) => {
      if (selected.length) {
        const opt = options.find((o) => o.value === selected[0]);
        if (opt) {
          setSelectedLabel(opt.label);
          setQuery("");
          onChange(opt.value);
        }
      }
    },
    [options, onChange]
  );

  const handleRemove = useCallback(() => {
    setSelectedLabel("");
    setQuery("");
    onChange("");
  }, [onChange]);

  const verticalContent = selectedLabel ? (
    <LegacyStack spacing="extraTight" alignment="center">
      <Tag onRemove={handleRemove}>{selectedLabel}</Tag>
    </LegacyStack>
  ) : null;

  if (!categoryId) {
    return (
      <div className="text-sm text-gray-500 p-2">
        {t("contract_category_not_configured")}
      </div>
    );
  }

  return (
    <Autocomplete
      options={filteredOptions}
      selected={value ? [value] : []}
      loading={loading}
      onSelect={handleSelect}
      emptyState={
        !loading && options.length === 0 ? (
          <div style={{ padding: "12px 16px", color: "#616161" }}>
            {t("no_documents_found")}
          </div>
        ) : undefined
      }
      textField={
        <Autocomplete.TextField
          prefix={<Icon source={SearchIcon as any} tone="base" />}
          label={t(label || "select_file")}
          value={query}
          autoComplete="off"
          onChange={setQuery}
          requiredIndicator={isRequired}
          verticalContent={verticalContent}
          placeholder={t("search_files")}
        />
      }
    />
  );
}

// ─── Contract Registration steps ──────────────────────────────────

export const contractDetailsStep: FunnelStep = {
  id: "contract-details",
  title: { lt: "Sutarties informacija", en: "Contract Details" },
  fields: [
    {
      name: "originalContractNumber",
      label: {
        lt: "Originalus sutarties numeris",
        en: "Original Contract Number",
      },
      type: "text",
      required: true,
    },
    {
      name: "personalCode",
      label: { lt: "Asmens kodas", en: "Personal Code" },
      type: "text",
      required: true,
      validation: { pattern: "^[0-9]{11}$" },
    },
    {
      // Optional here: the employee already has an account, so gender may
      // already be set on their user profile. HR can set/correct it if not.
      name: "gender",
      label: { lt: "Lytis", en: "Gender" },
      type: "select",
      helpText: {
        lt: "Naudojama, kad sutartyje pareigos būtų nurodytos tinkama gimine.",
        en: "Used to print the position title in the correct form in the contract.",
      },
      options: [
        { value: "male", label: { lt: "Vyras", en: "Male" } },
        { value: "female", label: { lt: "Moteris", en: "Female" } },
      ],
    },
    {
      name: "hiringDate",
      label: { lt: "Įdarbinimo data", en: "Hiring date" },
      type: "date",
      required: true,
    },
    {
      name: "contractType",
      label: { lt: "Sutarties tipas", en: "Contract type" },
      type: "select",
      options: [
        {
          value: "indefinite",
          label: { lt: "Neterminuota", en: "Indefinite" },
        },
        { value: "fixed_term", label: { lt: "Terminuota", en: "Fixed term" } },
        { value: "other", label: { lt: "Kita", en: "Other" } },
      ],
    },
    {
      name: "contractVariant",
      label: { lt: "Sutarties variantas", en: "Contract variant" },
      type: "select",
      options: [
        { value: "standard", label: { lt: "Standartinė", en: "Standard" } },
        { value: "summed", label: { lt: "Suminė", en: "Summed working time" } },
        { value: "director", label: { lt: "Direktoriaus", en: "Director" } },
      ],
    },
    {
      name: "contractEndDate",
      label: { lt: "Sutarties pabaigos data", en: "Contract end date" },
      type: "date",
      visibleWhen: {
        field: "contractType",
        operator: "eq",
        value: "fixed_term",
      },
    },
  ],
};

const positionStep: FunnelStep = {
  id: "position-compensation",
  title: { lt: "Pareigos ir atlyginimas", en: "Position & compensation" },
  fields: [
    {
      // Chosen from the positions catalogue; the backend derives positionLT/EN.
      name: "positionIds",
      label: { lt: "Pareigos", en: "Positions" },
      type: "custom",
      component: PositionsFunnelField,
    },
    {
      name: "salary",
      label: { lt: "Atlyginimas", en: "Salary" },
      type: "number",
      sensitive: true,
    },
    {
      name: "hoursPerWeek",
      label: { lt: "Valandos per savaitę", en: "Hours per week" },
      type: "number",
    },
    {
      name: "workLocation",
      label: { lt: "Darbo vieta", en: "Work location" },
      type: "text",
    },
  ],
};

const EmployeeSelectStep: React.FC<CustomComponentProps> = ({
  data,
  onChange,
  updateData,
}) => {
  const { companyId } = useParams<{ companyId: string }>();
  const latestManagerAutofillRequestRef = useRef(0);
  const [contractCategoryIds, setContractCategoryIds] =
    useState<ContractCategoryIds>({
      standard: "",
      director: "",
    });

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const companyData = await fetchCompanyById(companyId);
        if (cancelled) return;
        const entity = companyData?.companyEntity || companyData;
        setContractCategoryIds({
          standard: entity?.onboardingContractStandardCategory || "",
          director: entity?.onboardingContractDirectorCategory || "",
        });
      } catch {
        if (!cancelled) {
          setContractCategoryIds({ standard: "", director: "" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const activeContractCategoryId = useMemo(() => {
    if (data.contractVariant === "director") {
      return contractCategoryIds.director || contractCategoryIds.standard;
    }
    return contractCategoryIds.standard;
  }, [data.contractVariant, contractCategoryIds]);

  // Contract registration records an existing employee: start the positions
  // from what they hold now. The decision runs against the funnel's latest
  // data (this step may have unmounted by the time the answer lands) and the
  // prefill mark lives in that data too, so it survives step navigation.
  const prefillPositions = useCallback(
    async (userId: string) => {
      if (!companyId || !updateData) return;
      try {
        const current = await fetchUserPositions(companyId, userId);
        const ids = current.map((position) => position.id);
        updateData((prev) => {
          const next = decidePositionsPrefill(prev, userId, ids);
          return next ? { ...prev, ...next } : prev;
        });
      } catch {
        // No prefill — the picker stays as it is.
      }
    },
    [companyId, updateData]
  );

  const handleUserChange = useCallback(
    async (value: string) => {
      const requestId = latestManagerAutofillRequestRef.current + 1;
      latestManagerAutofillRequestRef.current = requestId;
      onChange("user", value);
      if (value && companyId) {
        prefillPositions(value);
        try {
          const { userEntity } = await fetchUserById(value, companyId);
          if (latestManagerAutofillRequestRef.current !== requestId) return;
          if (userEntity?.manager) {
            const mgr =
              typeof userEntity.manager === "object"
                ? userEntity.manager.id
                : userEntity.manager;
            if (mgr) {
              onChange("manager", mgr);
            }
          }
        } catch {
          // ignore manager auto-fill failures
        }
      }
    },
    [onChange, companyId, prefillPositions]
  );

  const handleManagerChange = useCallback(
    (value: string) => {
      onChange("manager", value);
    },
    [onChange]
  );

  const handleFileChange = useCallback(
    (value: string) => {
      onChange("fileId", value);
    },
    [onChange]
  );

  return (
    <div className="flex flex-col gap-4">
      <UserSelect
        onChange={handleUserChange}
        value={data.user || ""}
        label="employee"
        isRequired
      />
      <UserSelect
        onChange={handleManagerChange}
        value={data.manager || ""}
        label="manager"
        isRequired
        onlyActivated
      />
      <FileSelect
        value={data.fileId || ""}
        onChange={handleFileChange}
        label="contract_file"
        isRequired
        categoryId={activeContractCategoryId}
      />
    </div>
  );
};
EmployeeSelectStep.displayName = "EmployeeSelectStep";

// ─── Main component ───────────────────────────────────────────────

const OnboardingForm: React.FC<OnboardingFormProps> = ({ id }) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();

  const isEditMode = !!id;
  const role = useSelector((state: RootState) => state.authReducer.role);
  // Contract registration is HR-only; a hiring head goes straight to a new hire.
  const canRegisterContracts = canManageOnboarding(role);
  const [mode, setMode] = useState<OnboardingMode | null>(
    isEditMode || !canRegisterContracts ? "full" : null
  );
  const [initialData, setInitialData] = useState<Record<string, any> | null>(
    null
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasStepDocument, setHasStepDocument] = useState(false);
  const [manager, setManager] = useState("");
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>(
    []
  );
  const [currentDepartmentOptions, setCurrentDepartmentOptions] = useState<
    DepartmentOption[]
  >([]);
  const [deptMeta, setDeptMeta] =
    useState<AssignableDepartmentsResponse | null>(null);
  const [deptMetaError, setDeptMetaError] = useState(false);
  const [deptMetaReloadKey, setDeptMetaReloadKey] = useState(0);
  const managerRef = useRef("");

  const handleManagerChange = (value: string) => {
    managerRef.current = value;
    setManager(value);
  };

  useEffect(() => {
    if (!companyId) {
      setDeptMeta(null);
      setDeptMetaError(false);
      return;
    }
    let cancelled = false;
    setDeptMeta(null);
    setDeptMetaError(false);
    (async () => {
      try {
        const data = await fetchAssignableDepartments(companyId);
        if (!cancelled) setDeptMeta(data);
      } catch {
        // Surface the failure as a retryable error instead of leaving deptMeta
        // null forever: otherwise validateDepartments would block every submit
        // with a permanent "Loading…" and the form could never be saved.
        if (!cancelled) setDeptMetaError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, deptMetaReloadKey]);

  const reloadDeptMeta = () => setDeptMetaReloadKey((key) => key + 1);

  // A hiring head (not HR) must tag every hire with a department he heads.
  const requireDepartments = !!deptMeta?.requireDepartments;
  const blockCreate = deptMeta?.canCreateOnboarding === false;

  // A head with a single department gets it pre-selected on a new hire.
  useEffect(() => {
    if (isEditMode || !deptMeta?.requireDepartments) return;
    if (deptMeta.departments.length !== 1) return;
    const onlyDepartmentId = deptMeta.departments[0].id;
    setSelectedDepartmentIds((ids) => (ids.length ? ids : [onlyDepartmentId]));
  }, [isEditMode, deptMeta]);

  const validateDepartments = () => {
    // Block submission while assignable-department metadata is unavailable: the
    // caller's limits are unknown, so requireDepartments/blockCreate and
    // buildDepartmentIdsPayload would all behave as for HR (no limit). Both
    // submit handlers call this before building the payload, so this one guard
    // covers create and edit. Distinguish a hard load failure (retryable) from
    // an in-flight load so the user isn't stuck on a fake "Loading…".
    if (deptMetaError) {
      toast.error(
        t("onboarding_department_load_failed", {
          defaultValue:
            "Failed to load departments. Please retry before saving.",
        })
      );
      return false;
    }
    if (!deptMeta) {
      toast.error(t("loading", { defaultValue: "Loading…" }));
      return false;
    }
    if (!requireDepartments) return true;
    // Count only what is sent: locked tags outside the head's departments
    // stay on the record but are not part of the payload.
    if (buildDepartmentIdsPayload().length > 0) return true;
    toast.error(t("onboarding_department_required"));
    return false;
  };

  const showCreateBlockedError = () => {
    toast.error(t("onboarding_no_department_message"));
  };

  const departmentOptions = useMemo(() => {
    const byId = new Map<string, DepartmentOption>();
    [...(deptMeta?.departments || []), ...currentDepartmentOptions].forEach(
      (department) => {
        if (department.id) {
          byId.set(department.id, {
            id: department.id,
            name: department.name,
          });
        }
      }
    );
    return Array.from(byId.values());
  }, [currentDepartmentOptions, deptMeta]);

  const assignableDepartmentIds = useMemo(
    () =>
      new Set((deptMeta?.departments || []).map((department) => department.id)),
    [deptMeta?.departments]
  );

  const scopedLockedDepartmentIds = useMemo(() => {
    if (!requireDepartments) return [];
    return currentDepartmentOptions
      .map((department) => department.id)
      .filter((id) => id && !assignableDepartmentIds.has(id));
  }, [assignableDepartmentIds, currentDepartmentOptions, requireDepartments]);

  const handleDepartmentIdsChange = (ids: string[]) => {
    if (!scopedLockedDepartmentIds.length) {
      setSelectedDepartmentIds(ids);
      return;
    }
    setSelectedDepartmentIds(
      Array.from(new Set([...ids, ...scopedLockedDepartmentIds]))
    );
  };

  // A hiring head only submits the departments he heads (assignable); the
  // server preserves out-of-scope departments via merge. HR's selection is
  // taken verbatim, so HR sends the full selection.
  // Without this, editing a record that carries a department outside the
  // editor's scope echoes that id back and validateEditDepartments rejects the
  // whole PATCH with a 403.
  const buildDepartmentIdsPayload = () => {
    if (!requireDepartments) {
      return selectedDepartmentIds;
    }
    return selectedDepartmentIds.filter((id) =>
      assignableDepartmentIds.has(id)
    );
  };

  const departmentSection = (
    <div className="mb-4">
      {deptMetaError && (
        <Banner
          tone="critical"
          action={{
            content: t("retry", { defaultValue: "Retry" }),
            onAction: reloadDeptMeta,
          }}
        >
          <p>
            {t("onboarding_department_load_failed", {
              defaultValue:
                "Failed to load departments. Please retry before saving.",
            })}
          </p>
        </Banner>
      )}
      {blockCreate && (
        <Banner tone="warning">
          <p>{t("onboarding_no_department_message")}</p>
        </Banner>
      )}
      {!blockCreate && !deptMetaError && (
        <DepartmentMultiSelect
          companyId={companyId || ""}
          value={selectedDepartmentIds}
          onChange={handleDepartmentIdsChange}
          disabled={!deptMeta}
          required={requireDepartments}
          options={departmentOptions}
        />
      )}
    </div>
  );

  // Load edit data
  useEffect(() => {
    if (isEditMode && companyId && id) {
      (async () => {
        try {
          const data = await fetchOnboardingById(companyId, id);
          const dataMode = data.mode || "full";
          setMode(dataMode);

          const selectedManager =
            typeof data.manager === "object"
              ? data.manager?.id || ""
              : data.manager || "";
          managerRef.current = selectedManager;
          setManager(selectedManager);
          const departments = (data.departments || []).map(
            (department: DepartmentOption) => ({
              id: department.id,
              name: department.name,
            })
          );
          setCurrentDepartmentOptions(departments);

          if (dataMode === "contract_registration") {
            const userId =
              typeof data.user === "object"
                ? data.user?.id || ""
                : data.user || "";
            const contractFile = (data.files || []).find(
              (f: any) => f.fileType === "step_4_pdf"
            );
            const fileId = contractFile
              ? typeof contractFile.file === "object"
                ? contractFile.file.id
                : contractFile.file
              : "";

            setInitialData({
              user: userId,
              manager: selectedManager,
              fileId: fileId || "",
              originalContractNumber:
                (data as any).originalContractNumber || "",
              personalCode: data.personalCode || "",
              // Optional in registration mode: prefill from the employee's
              // profile when the backend returns it, empty otherwise.
              gender: data.gender || "",
              hiringDate: data.hiringDate || null,
              contractType: data.contractType || "indefinite",
              contractVariant: data.contractVariant || "standard",
              contractEndDate: data.contractEndDate || null,
              positionIds: parsePositionIds(data.positionIds),
              // Read-only: only the picker's hint for a pre-catalogue record.
              positionLT: data.positionLT || "",
              salary: data.salary || null,
              hoursPerWeek: data.hoursPerWeek ?? 40,
              workLocation: data.workLocation || "",
            });
            setSelectedDepartmentIds(
              departments.map((department) => department.id)
            );
          } else {
            const stepFileTypes =
              data.status === "step_2"
                ? STEP_2_FILE_TYPES
                : data.status
                ? [`${data.status}_pdf`]
                : [];
            const hasDoc = (data.files || []).some((f: any) =>
              stepFileTypes.includes(f.fileType)
            );
            setHasStepDocument(!!hasDoc);

            setInitialData({
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              personalEmail: data.personalEmail || "",
              personalPhone: data.personalPhone || "",
              // Required in full mode; the backend returns the user's current
              // gender to onboarding managers — may be absent/null, then empty.
              gender: data.gender || "",
              hiringDate: data.hiringDate || null,
              birthDate: data.birthDate || null,
              workLocation: data.workLocation || "",
              positionIds: parsePositionIds(data.positionIds),
              // Read-only: only the picker's hint for a pre-catalogue record.
              positionLT: data.positionLT || "",
              salary: data.salary || null,
              hoursPerWeek: data.hoursPerWeek ?? 40,
              probationMonths: data.probationMonths ?? null,
              // hasSalaryChange is UI-only — derived from whether a second
              // salary is already recorded, never sent to the backend as-is.
              hasSalaryChange: !!data.salaryChangeDate,
              salaryChangeDate: data.salaryChangeDate ?? null,
              salaryAfterChange: data.salaryAfterChange ?? null,
              contractVariant: data.contractVariant || "standard",
              contractType: data.contractType || "indefinite",
              contractEndDate: data.contractEndDate || null,
              contractTypeOther: data.contractTypeOther || "",
            });
            setSelectedDepartmentIds(
              departments.map((department) => department.id)
            );
          }
        } catch {
          setLoadError("error");
        }
      })();
    }
  }, [isEditMode, companyId, id]);

  // Set initial data when mode is selected for create
  useEffect(() => {
    if (!isEditMode && mode === "full") {
      setInitialData({
        hoursPerWeek: 40,
        probationMonths: null,
        hasSalaryChange: false,
        salaryChangeDate: null,
        salaryAfterChange: null,
        contractType: "indefinite",
        contractVariant: "standard",
        birthDate: null,
        positionIds: [],
      });
    } else if (!isEditMode && mode === "contract_registration") {
      setInitialData({
        user: "",
        manager: "",
        fileId: "",
        hoursPerWeek: 40,
        contractType: "indefinite",
        contractVariant: "standard",
        positionIds: [],
      });
    }
  }, [isEditMode, mode]);

  // ─── Mode selector ─────────────────────────────────────────────
  if (!isEditMode && mode === null) {
    return (
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-4">{t("create_onboarding")}</h2>
        {blockCreate && (
          <div className="mb-4">
            <Banner tone="warning">
              <p>{t("onboarding_no_department_message")}</p>
            </Banner>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <button
            className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left disabled:opacity-50 disabled:pointer-events-none"
            onClick={() => setMode("full")}
            disabled={blockCreate}
          >
            <div className="text-lg font-medium mb-2">{t("new_hire")}</div>
            <div className="text-sm text-gray-500">
              {t("new_hire_description")}
            </div>
          </button>
          <button
            className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left disabled:opacity-50 disabled:pointer-events-none"
            onClick={() => setMode("contract_registration")}
            disabled={blockCreate}
          >
            <div className="text-lg font-medium mb-2">
              {t("contract_registration")}
            </div>
            <div className="text-sm text-gray-500">
              {t("contract_registration_description")}
            </div>
          </button>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
        {t(loadError)}
      </div>
    );
  }

  if (!initialData) {
    return (
      <div className="flex justify-center p-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // ─── Contract Registration form (create & edit) ─────────────────
  if (mode === "contract_registration") {
    const handleRegistrationSubmit = async (data: Record<string, any>) => {
      if (!isEditMode && blockCreate) {
        showCreateBlockedError();
        return;
      }
      if (!validateDepartments()) return;

      const selectedUser = data.user || "";
      const selectedManager = data.manager || "";
      const selectedFile = data.fileId || "";

      if (!selectedUser) {
        toast.error(t("employee_required"));
        return;
      }
      if (!selectedManager) {
        toast.error(t("manager_required"));
        return;
      }
      if (!selectedFile) {
        toast.error(t("contract_file_required"));
        return;
      }
      if (!data.originalContractNumber?.trim()) {
        toast.error(t("original_contract_number_required"));
        return;
      }
      if (!data.personalCode?.trim()) {
        toast.error(t("personal_code_required"));
        return;
      }

      const payload: any = {
        originalContractNumber: data.originalContractNumber.trim(),
        personalCode: data.personalCode.trim(),
        hiringDate:
          typeof data.hiringDate === "number" ? data.hiringDate : null,
        contractType: data.contractType || "indefinite",
        contractVariant: data.contractVariant || "standard",
        contractEndDate:
          data.contractType === "fixed_term" && data.contractEndDate
            ? data.contractEndDate
            : null,
        // An empty list is left out: for a registration the backend then
        // keeps the employee's current positions.
        ...(Array.isArray(data.positionIds) && data.positionIds.length > 0
          ? { positionIds: data.positionIds }
          : {}),
        // Optional here: omit entirely when untouched/empty so the backend
        // keeps whatever gender is already on the employee's user profile.
        ...(data.gender ? { gender: data.gender } : {}),
        salary: data.salary ? parseFloat(data.salary) || null : null,
        hoursPerWeek: data.hoursPerWeek
          ? parseFloat(data.hoursPerWeek) || 40
          : 40,
        workLocation: data.workLocation?.trim() || null,
        user: selectedUser,
        manager: selectedManager,
        fileId: selectedFile || undefined,
        departmentIds: buildDepartmentIdsPayload(),
      };

      try {
        if (isEditMode && companyId && id) {
          await updateOnboarding(companyId, id, payload);
          dispatch(fetchOnboardingDetails({ companyId, onboardingId: id }));
          dispatch(setIsModalxOpen(false));
        } else if (companyId) {
          payload.mode = "contract_registration";
          const created: any = await createOnboarding(companyId, payload);
          dispatch(setIsModalxOpen(false));
          navigate(
            `/app/${companyId}/onboarding/${
              created.onboarding?.id || created.id
            }`
          );
        }
      } catch (e: any) {
        const code = e?.response?.data?.error;
        if (code === "signed_contract_locked") {
          toast.error(t("signed_contract_locked"));
        } else if (errorCode(e) === "POSITIONS_REQUIRED") {
          toast.error(t("positions_required"));
        } else if (errorCode(e) === "INVALID_GENDER") {
          toast.error(t("invalid_gender"));
        } else {
          toast.error(code || t("error_creating_registration"));
        }
      }
    };

    const registrationSteps: FunnelStep[] = [
      {
        id: "employee-select",
        title: { lt: "Darbuotojas ir sutartis", en: "Employee & Contract" },
        fields: [],
        customComponent: EmployeeSelectStep,
      },
      contractDetailsStep,
      positionStep,
    ];

    const funnelConfig: FunnelConfig = {
      id: "onboarding-registration",
      steps: registrationSteps,
      autoSave: false,
      initialData,
      onSave: async () => {},
      onSubmit: handleRegistrationSubmit,
    };

    return (
      <div className="p-2">
        <div className="flex items-center gap-2 mb-4">
          {!isEditMode && (
            <>
              <button
                className="text-sm text-blue-600 hover:underline max-md:min-h-11 max-md:pr-2"
                onClick={() => {
                  setMode(null);
                  setInitialData(null);
                  setSelectedDepartmentIds([]);
                }}
              >
                &larr; {t("back")}
              </button>
              <span className="text-sm text-gray-400">|</span>
            </>
          )}
          <span className="text-sm font-medium text-gray-700">
            {t("contract_registration")}
          </span>
        </div>
        {departmentSection}
        <FormFunnel config={funnelConfig} />
      </div>
    );
  }

  // ─── Full onboarding form (existing logic) ─────────────────────
  const buildPayload = (data: Record<string, any>) => ({
    firstName: data.firstName?.trim(),
    lastName: data.lastName?.trim(),
    personalEmail: data.personalEmail?.trim().toLowerCase(),
    personalPhone: data.personalPhone?.trim(),
    gender: data.gender,
    hiringDate: typeof data.hiringDate === "number" ? data.hiringDate : null,
    birthDate: typeof data.birthDate === "number" ? data.birthDate : null,
    workLocation: data.workLocation?.trim(),
    positionIds: Array.isArray(data.positionIds) ? data.positionIds : [],
    salary:
      typeof data.salary === "number"
        ? data.salary
        : parseFloat(data.salary) || null,
    hoursPerWeek:
      typeof data.hoursPerWeek === "number"
        ? data.hoursPerWeek
        : parseFloat(data.hoursPerWeek) || 40,
    probationMonths:
      data.probationMonths === "" || data.probationMonths == null
        ? null
        : typeof data.probationMonths === "number"
        ? data.probationMonths
        : parseInt(data.probationMonths),
    contractType: data.contractType,
    contractVariant: data.contractVariant,
    contractEndDate:
      data.contractType === "fixed_term" && data.contractEndDate
        ? data.contractEndDate
        : null,
    contractTypeOther:
      data.contractType === "other" ? data.contractTypeOther : null,
    manager: managerRef.current || null,
    ...buildSalaryChangePayload(data),
  });

  const funnelConfig: FunnelConfig = {
    id: "onboarding-step1",
    steps: step1Steps,
    autoSave: false,
    initialData,
    onSave: async () => {},
    onSubmit: async (data) => {
      if (!isEditMode && blockCreate) {
        showCreateBlockedError();
        return;
      }
      if (!validateDepartments()) return;
      if (!managerRef.current) {
        toast.error(t("manager_required"));
        return;
      }
      const payload = {
        ...buildPayload(data),
        departmentIds: buildDepartmentIdsPayload(),
      };
      // Mirrors the backend's SALARY_CHANGE_INVALID check (§4) so the editor
      // sees it immediately instead of a round trip — the backend stays the
      // authority and is checked again below.
      const salaryChangeError = validateSalaryChangePayload(
        { salaryChangeDate: payload.salaryChangeDate, salaryAfterChange: payload.salaryAfterChange },
        { hiringDate: payload.hiringDate, contractEndDate: payload.contractEndDate }
      );
      if (salaryChangeError) {
        throw new Error(salaryChangeError);
      }
      if (isEditMode && companyId && id) {
        try {
          await updateOnboarding(companyId, id, payload);
        } catch (e: any) {
          // Re-thrown as a plain Error so FormFunnel's t(submitError) shows
          // the right text — axios errors carry a generic e.message, not
          // the backend's error code.
          if (e?.response?.data?.error === "signed_contract_locked") {
            throw new Error("signed_contract_locked");
          }
          throwIfAlreadyEmployedHere(e);
          if (errorCode(e) === "POSITIONS_REQUIRED") {
            throw new Error("positions_required");
          }
          if (errorCode(e) === "INVALID_GENDER") {
            throw new Error("invalid_gender");
          }
          if (errorCode(e) === "SALARY_CHANGE_INVALID") {
            throw new Error(salaryChangeErrorMessageKey(e));
          }
          const departmentKey = onboardingDepartmentErrorKey(e);
          if (departmentKey) {
            throw new Error(departmentKey);
          }
          throw e;
        }
        dispatch(fetchOnboardingDetails({ companyId, onboardingId: id }));
      } else if (companyId) {
        let created: any;
        try {
          created = await createOnboarding(companyId, payload);
        } catch (e: any) {
          // FormFunnel shows t(message): hand it a translation key.
          if (errorCode(e) === "POSITIONS_REQUIRED") {
            throw new Error("positions_required");
          }
          if (errorCode(e) === "INVALID_GENDER") {
            throw new Error("invalid_gender");
          }
          if (errorCode(e) === "SALARY_CHANGE_INVALID") {
            throw new Error(salaryChangeErrorMessageKey(e));
          }
          if (errorCode(e) === "EMPLOYEE_ALREADY_ACTIVE") {
            throw new Error("onboarding_employee_already_active");
          }
          throwIfAlreadyEmployedHere(e);
          const departmentKey = onboardingDepartmentErrorKey(e);
          if (departmentKey) {
            throw new Error(departmentKey);
          }
          throw e;
        }
        dispatch(setIsModalxOpen(false));
        navigate(
          `/app/${companyId}/onboarding/${created.onboarding?.id || created.id}`
        );
        return;
      }
      dispatch(setIsModalxOpen(false));
    },
  };

  return (
    <>
      {/* Back to the new hire / contract registration choice — HR only has one. */}
      {!isEditMode && canRegisterContracts && (
        <div className="flex items-center gap-2 mb-4 px-2">
          <button
            className="text-sm text-blue-600 hover:underline max-md:min-h-11 max-md:pr-2"
            onClick={() => {
              setMode(null);
              setInitialData(null);
              setSelectedDepartmentIds([]);
            }}
          >
            &larr; {t("back")}
          </button>
          <span className="text-sm text-gray-400">|</span>
          <span className="text-sm font-medium text-gray-700">
            {t("new_hire")}
          </span>
        </div>
      )}
      {isEditMode && hasStepDocument && (
        <div className="mb-4">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 rounded-lg">
            {t("edit_will_delete_document")}
          </div>
        </div>
      )}
      {departmentSection}
      <div className="mb-4">
        <UserSelect
          onChange={handleManagerChange}
          value={manager}
          label="manager"
          isRequired
          onlyActivated
        />
      </div>
      <FormFunnel config={funnelConfig} />
    </>
  );
};

export default OnboardingForm;
